import { AxleFieldNames } from '@contracts/order/fields/AxleInformationFields';
import { TrailerFieldNames } from '@contracts/order/fields/TrailerInformationFields';
import { TrailerFormName as ContractsTrailerFormName } from '@contracts/order/OrderForm';
import { PlateType } from '@contracts/order/valueNormalizers/PlateType';
import { State } from '@contracts/order/valueNormalizers/State';
import { TrailerMake } from '@contracts/order/valueNormalizers/TrailerMake';
import { TrailerType } from '@contracts/order/valueNormalizers/TrailerType';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import {
  DropdownField,
  OptionalBooleanField,
  PositiveIntegerField,
  StringField,
} from '@src/permitField/PermitFields';
import type { StringSchema } from 'yup';
import * as yup from 'yup';

export const TrailersFormName = ContractsTrailerFormName;
export const TrailerFormUtils = {
  pathToTrailer: (index: number) => `${TrailersFormName}.${index}`,
  pathToTrailerAxles: (trailerIndex: number) =>
    `${TrailersFormName}.${trailerIndex}.${AxleFormName}`,
};

export class TrailerIdField extends StringField {
  public static fieldName = TrailerFieldNames.TrailerId;
}

export class IsDualLaneField extends OptionalBooleanField {
  public static fieldName = TrailerFieldNames.IsDualLane;
}

export class IsTrunnionAxleField extends OptionalBooleanField {
  public static fieldName = TrailerFieldNames.IsTrunnionAxle;
}

export class TrailerUnitNumberField extends StringField {
  public static fieldName = TrailerFieldNames.TrailerUnitNumber;
}

export class TrailerYearField extends PositiveIntegerField {
  public static fieldName = TrailerFieldNames.TrailerYear;
}

export class TrailerMakeField extends StringField {
  public static fieldName = TrailerFieldNames.TrailerMake;
  public static options = TrailerMake;
  public static validator = yup.string().required();
}

export class TrailerVINField extends StringField {
  public static fieldName = TrailerFieldNames.TrailerVIN;
  public static validator: StringSchema = yup
    .string()
    .trim()
    .transform((value: string) => value.toUpperCase())
    .when(TrailerYearField.fieldName, {
      is: (year: number) => year > 1980,
      then: (schema) =>
        schema
          .length(17, 'isVin')
          .matches(/^[A-Z0-9]+$/, { message: 'isVin' })
          .required(),
      otherwise: (schema) => schema.trim().required(),
    });
}

export class TrailerTypeField extends DropdownField {
  public static fieldName = TrailerFieldNames.TrailerType;
  public static options = TrailerType;
  public static validator = yup
    .string()
    .oneOf(TrailerTypeField.options)
    .required();
}

export class TrailerAxleCountField extends DropdownField {
  public static fieldName = TrailerFieldNames.TrailerAxleCount;
  public static options = Array.from({ length: 21 }, (_, i) =>
    (i + 1).toString(),
  );
  public static validator = yup
    .string()
    .oneOf(TrailerAxleCountField.options)
    .transform((value: string) =>
      value === '' || value === '0' ? undefined : value,
    )
    .when(AxleFormName, {
      is: (axles: unknown[]) => !axles || axles.length === 0,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    });
}

export class TrailerLicensePlateField extends StringField {
  public static fieldName = TrailerFieldNames.TrailerLicensePlate;
}

export class TrailerLicensePlateStateField extends DropdownField {
  public static fieldName = TrailerFieldNames.TrailerLicensePlateState;
  public static options = Object.values(State);
  public static validator = yup
    .string()
    .oneOf(TrailerLicensePlateStateField.options)
    .required();
}

export class TrailerLicensePlateTypeField extends DropdownField {
  public static fieldName = TrailerFieldNames.TrailerLicensePlateType;
  public static options = PlateType;
  public static validator = yup
    .string()
    .oneOf(TrailerLicensePlateTypeField.options)
    .required();
}

export const TrailerCountFieldName = 'trailerCount';

export const shouldDisplayTrailerAxlesCount = (fields: string[]) => {
  return (
    fields.includes(TrailerAxleCountField.fieldName) && hasNoAxlesFields(fields)
  );
};

const hasNoAxlesFields = (fields: string[]) => {
  const axleFields = new Set<string>(Object.values(AxleFieldNames) as string[]);
  return fields.every((field) => !axleFields.has(field));
};
